import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Tag, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

// Predefined tag options with colors
const AVAILABLE_TAGS = [
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  { value: 'deliverability', label: 'Deliverability', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'authentication', label: 'Authentication', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'blacklist', label: 'Blacklist', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { value: 'account', label: 'Account', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  { value: 'bug', label: 'Bug Report', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  { value: 'feature', label: 'Feature Request', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { value: 'resolved', label: 'Resolved', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { value: 'waiting', label: 'Waiting on User', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
];

interface ConversationTagsProps {
  conversationId: string;
  currentTags: string[];
  onTagsUpdated: (tags: string[]) => void;
}

export const ConversationTags = ({ conversationId, currentTags, onTagsUpdated }: ConversationTagsProps) => {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  const getTagConfig = (tag: string) => {
    return AVAILABLE_TAGS.find(t => t.value === tag) || { 
      value: tag, 
      label: tag, 
      color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' 
    };
  };

  const handleAddTag = async (tagValue: string) => {
    if (currentTags.includes(tagValue)) return;
    
    setUpdating(true);
    const newTags = [...currentTags, tagValue];
    
    const { error } = await supabase
      .from('support_conversations')
      .update({ tags: newTags })
      .eq('id', conversationId);

    if (error) {
      toast({
        title: 'Failed to add tag',
        variant: 'destructive',
      });
    } else {
      onTagsUpdated(newTags);
    }
    setUpdating(false);
  };

  const handleRemoveTag = async (tagValue: string) => {
    setUpdating(true);
    const newTags = currentTags.filter(t => t !== tagValue);
    
    const { error } = await supabase
      .from('support_conversations')
      .update({ tags: newTags })
      .eq('id', conversationId);

    if (error) {
      toast({
        title: 'Failed to remove tag',
        variant: 'destructive',
      });
    } else {
      onTagsUpdated(newTags);
    }
    setUpdating(false);
  };

  const availableToAdd = AVAILABLE_TAGS.filter(t => !currentTags.includes(t.value));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {currentTags.map((tag) => {
        const config = getTagConfig(tag);
        return (
          <Badge
            key={tag}
            variant="secondary"
            className={`${config.color} text-xs px-2 py-0.5 gap-1`}
          >
            {config.label}
            <button
              onClick={() => handleRemoveTag(tag)}
              disabled={updating}
              className="ml-0.5 hover:opacity-70 transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        );
      })}
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
            disabled={updating || availableToAdd.length === 0}
          >
            <Plus className="w-3 h-3 mr-0.5" />
            Tag
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-48 p-2">
          <div className="space-y-1">
            {availableToAdd.map((tag) => (
              <button
                key={tag.value}
                onClick={() => {
                  handleAddTag(tag.value);
                  setOpen(false);
                }}
                className={`w-full text-left px-2 py-1.5 rounded text-xs ${tag.color} hover:opacity-80 transition-opacity`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Export available tags for filtering
export { AVAILABLE_TAGS };
